/**
 * Turning a brand profile into the answers the chrome asks for.
 *
 * Two functions, deliberately not one, and the split is the same one
 * `vite.config.ts` already makes when it refuses to build a bundle pointing at
 * a plaintext backend: **fail the deploy, never the user.**
 *
 * | | when | on a bad profile |
 * |---|---|---|
 * | `checkProfile` | build, and in the studio as you type | reports every problem |
 * | `resolveBrand` | once at module load, in the browser | clamps, and renders |
 *
 * `resolveBrand` is total. It never throws and never returns nothing, because
 * the one thing worse than an app with a feature in the wrong place is a white
 * screen — and by the time it runs, the build that should have caught the
 * problem has already shipped. An unknown feature id is ignored, an illegal
 * slot falls back to the registry default, a disabled core feature stays on.
 * Every one of those is also a `checkProfile` error, so the loud report happens
 * where somebody can still fix it.
 *
 * ## Absent means today
 *
 * `resolveBrand()` with no profile resolves to the app exactly as it is — same
 * name, same labels, same placements. That is not a convenience; it is what
 * makes this change additive. Until a profile exists, nothing about the running
 * app is different, and the first brand is a file rather than a migration.
 *
 * ## What it does not decide
 *
 * Nothing here is enforcement, and nothing here is async. A brand hides a
 * control; it does not refuse the request behind it. The server's entitlement
 * check is the one that matters, it runs on a different axis, and the two
 * intersect with the brand applied first — see `brand-registry.ts`.
 */

import type { BrandProfile, BrandFeatureOverride, Theme } from "@qufolio/model";

import {
  FEATURES,
  SHELLS,
  capacityOf,
  defaultSlot,
  featureById,
  slotsFor,
  type AnySlot,
  type FeatureDef,
  type FeatureId,
  type Shell,
} from "./brand-registry";

// ── The resolved shape ──────────────────────────────────────────────────

/** Where one feature ended up, in both shells. `null` is "no chrome here". */
export type ResolvedSlots = { readonly [S in Shell]: AnySlot | null };

export interface ResolvedFeature {
  readonly id: FeatureId;
  /** The brand's label, or the registry's. Never blank. */
  readonly label: string;
  readonly enabled: boolean;
  /**
   * Set when this feature is off because something it `requires` is off, rather
   * than because the profile said so. Not a fault — turning off the journal
   * legitimately turns off the plan — but the studio shows it so that the one
   * switch somebody flipped explains the three rows that greyed out.
   */
  readonly disabledBy?: FeatureId;
  readonly slots: ResolvedSlots;
  readonly order: number;
}

export interface ResolvedIdentity {
  readonly name: string;
  readonly shortName: string;
  readonly tagline: string;
  readonly domain?: string;
  readonly icon?: string;
  /** The two pre-React colours. Always present: the default is Classic's. */
  readonly color: { readonly background: string; readonly text: string };
  /** The backdrop behind the sign-in screen; absent, the plain background. */
  readonly signInBackdrop?: "sweetertrade" | "qufolio";
}

/**
 * The themes this app offers.
 *
 * `allow` is a *filter over ids*, not a list of documents, because this module
 * has no access to the built-in themes — it may import only types from
 * `@qufolio/model`, or the build's config could not load it. `lib/brand-themes.ts`
 * is where ids become `Theme`s, and it is the only place that needs both.
 */
export interface ResolvedThemes {
  /** Id a workspace opens in when it names none, or `null` for the app default. */
  readonly defaultRef: string | null;
  /** Built-in ids this brand offers, or `null` meaning all of them. */
  readonly allow: readonly string[] | null;
  /** Theme documents this brand ships. Empty unless the build inlined some. */
  readonly custom: readonly Theme[];
}

export interface ResolvedBrand {
  readonly key: string;
  readonly identity: ResolvedIdentity;
  readonly themes: ResolvedThemes;
  readonly backend: BrandProfile["backend"];
  readonly features: Readonly<Record<FeatureId, ResolvedFeature>>;
  /** Whether a feature renders at all in this app. */
  enabled(id: FeatureId): boolean;
  /** What this brand calls a feature. */
  label(id: FeatureId): string;
  /** Where a feature sits in one shell, or `null` if it has no chrome there. */
  slotOf(id: FeatureId, shell: Shell): AnySlot | null;
  /**
   * The enabled features in one slot, ordered — what a surface renders.
   *
   * Ordered by `order` then registry position, so a brand that sets no orders
   * gets the order this file lists features in, and a brand that sets some gets
   * those first without having to number the rest.
   */
  inSlot(shell: Shell, slot: AnySlot): readonly ResolvedFeature[];
}

/**
 * The app with no profile: QuFolio, as `index.html` and the PWA manifest say
 * it today. These three literals are the one place the unbranded name lives,
 * and the build reads them rather than the `<title>` tag it rewrites.
 */
export const DEFAULT_IDENTITY: ResolvedIdentity = {
  name: "QuFolio",
  shortName: "QuFolio",
  tagline: "Charts, screener and trading journal.",
  // Classic's `background` and `text` — the same two literals
  // `index.html` carries, and for the reason its own comment gives: they paint
  // the frames before any provider exists, so they cannot name a token.
  color: { background: "#0e1116", text: "#e6e9ef" },
};

/**
 * The registry's own order, or its position × 10 where it states none.
 *
 * Sparse either way, so a brand can insert between two features without
 * renumbering the rest.
 */
function defaultOrder(def: FeatureDef, index: number): number {
  return def.order ?? index * 10;
}

// ── resolveBrand ────────────────────────────────────────────────────────

interface Draft {
  def: FeatureDef;
  index: number;
  label: string;
  enabled: boolean;
  disabledBy?: FeatureId;
  slots: { desktop: AnySlot | null; mobile: AnySlot | null };
  order: number;
}

/** A label is only an override if it says something. `"  "` is not a name. */
function pickLabel(def: FeatureDef, over: BrandFeatureOverride | undefined): string {
  const given = over?.label?.trim();
  return given ? given : def.label;
}

/** The named slot if the feature may legally occupy it, else the default. */
function pickSlot(
  def: FeatureDef,
  shell: Shell,
  over: BrandFeatureOverride | undefined,
): AnySlot | null {
  const allowed = slotsFor(def, shell);
  if (allowed.length === 0) return null;
  const asked = over?.slots?.[shell];
  return asked && (allowed as readonly string[]).includes(asked)
    ? (asked as AnySlot)
    : defaultSlot(def, shell);
}

/**
 * Take down everything that depends on something already down, to a fixpoint.
 *
 * A fixpoint rather than one pass because requirements chain: if C requires B
 * and B requires A, disabling A has to reach C, and a single sweep in registry
 * order only reaches it when the rows happen to be in the right sequence.
 * Bounded by the feature count, since each round turns at least one more off.
 */
function cascadeRequires(drafts: Map<string, Draft>): void {
  for (let round = 0; round < drafts.size; round++) {
    let changed = false;
    for (const draft of drafts.values()) {
      if (!draft.enabled || draft.def.core) continue;
      for (const needed of draft.def.requires ?? []) {
        const on = drafts.get(needed);
        // A requirement naming nothing is a registry bug, not a brand's doing,
        // and taking the feature down for it would turn a typo in this file
        // into a missing screen. `checkRegistry` is where that surfaces.
        if (on && !on.enabled) {
          draft.enabled = false;
          draft.disabledBy = (on.disabledBy ?? needed) as FeatureId;
          changed = true;
          break;
        }
      }
    }
    if (!changed) return;
  }
}

/**
 * Move the overflow out of a slot that holds a fixed number of things.
 *
 * The tab bar has room for three. A brand that puts a fourth feature there has
 * asked for something the shell cannot draw, so the ones past the cap move to
 * the next slot they are legally allowed — `more`, in practice — and to
 * `hidden` if there is no other. Sorted by the same rule the surface renders
 * by, so what survives is the first three a person would have expected.
 *
 * `checkProfile` calls this an error. This exists for the build that shipped
 * without running it.
 */
function spillOverflow(drafts: Map<string, Draft>, shell: Shell): void {
  const bySlot = new Map<AnySlot, Draft[]>();
  for (const draft of drafts.values()) {
    if (!draft.enabled) continue;
    const slot = draft.slots[shell];
    if (!slot) continue;
    const held = bySlot.get(slot);
    if (held) held.push(draft);
    else bySlot.set(slot, [draft]);
  }

  for (const [slot, held] of bySlot) {
    const cap = capacityOf(shell, slot);
    if (cap === null || held.length <= cap) continue;
    held.sort((a, b) => a.order - b.order || a.index - b.index);
    for (const draft of held.slice(cap)) {
      const alternative = slotsFor(draft.def, shell).find((s) => {
        if (s === slot) return false;
        const room = capacityOf(shell, s);
        return room === null || (bySlot.get(s)?.length ?? 0) < room;
      });
      draft.slots[shell] = (alternative ?? "hidden") as AnySlot;
      if (alternative) bySlot.get(alternative)?.push(draft);
    }
  }
}

/**
 * Resolve a profile into the answers the chrome asks for. Never throws.
 *
 * Pure, synchronous and cheap — call it once at module load with the profile
 * the build inlined, and hold the result.
 */
export function resolveBrand(
  profile?: BrandProfile,
  /**
   * The brand's own theme documents, read from the paths in `themes.custom`.
   *
   * Passed in rather than loaded, because loading is file I/O and this module
   * runs in a browser. The build reads them and inlines them; the studio has
   * them already; a test hands in whatever it is testing.
   */
  custom: readonly Theme[] = [],
): ResolvedBrand {
  const overrides = profile?.features ?? {};

  const drafts = new Map<string, Draft>();
  FEATURES.forEach((def, index) => {
    // An override naming a feature that does not exist is dropped here rather
    // than merged into nothing: `checkProfile` reports it, and a typo must not
    // quietly become a feature.
    const over = overrides[def.id];
    drafts.set(def.id, {
      def,
      index,
      label: pickLabel(def, over),
      // Core is not negotiable, at any layer, by anybody.
      enabled: def.core ? true : (over?.enabled ?? true),
      slots: {
        desktop: pickSlot(def, "desktop", over),
        mobile: pickSlot(def, "mobile", over),
      },
      order: over?.order ?? defaultOrder(def, index),
    });
  });

  cascadeRequires(drafts);
  for (const shell of SHELLS) spillOverflow(drafts, shell);

  const features = {} as Record<FeatureId, ResolvedFeature>;
  for (const draft of drafts.values()) {
    features[draft.def.id as FeatureId] = {
      id: draft.def.id as FeatureId,
      label: draft.label,
      enabled: draft.enabled,
      ...(draft.disabledBy ? { disabledBy: draft.disabledBy } : {}),
      slots: { desktop: draft.slots.desktop, mobile: draft.slots.mobile },
      order: draft.order,
    };
  }

  const id = profile?.identity;
  const name = id?.name?.trim() || DEFAULT_IDENTITY.name;
  const identity: ResolvedIdentity = {
    name,
    // A short name defaults to the full one. Not truncated to fit: a silently
    // clipped brand is worse than a long one, and `checkProfile` says so where
    // it can still be answered with a shorter word.
    shortName: id?.shortName?.trim() || name,
    tagline: id?.tagline?.trim() || DEFAULT_IDENTITY.tagline,
    ...(id?.domain ? { domain: id.domain } : {}),
    ...(id?.icon ? { icon: id.icon } : {}),
    ...(id?.signInBackdrop ? { signInBackdrop: id.signInBackdrop } : {}),
    color: {
      background: id?.color?.background || DEFAULT_IDENTITY.color.background,
      text: id?.color?.text || DEFAULT_IDENTITY.color.text,
    },
  };

  const themes: ResolvedThemes = {
    defaultRef: profile?.themes?.default?.trim() || null,
    // `null` and `[]` mean opposite things and both are legal: absent is "every
    // built-in", empty is "none of them, only what this brand brings". A `??`
    // anywhere on this path would silently turn the second into the first.
    allow: profile?.themes?.allow ?? null,
    custom,
  };

  return {
    key: profile?.key ?? "qufolio",
    identity,
    themes,
    backend: profile?.backend,
    features,
    enabled: (featureId) => features[featureId]?.enabled ?? false,
    label: (featureId) => features[featureId]?.label ?? featureId,
    slotOf: (featureId, shell) => features[featureId]?.slots[shell] ?? null,
    inSlot: (shell, slot) =>
      Object.values(features)
        .filter((f) => f.enabled && f.slots[shell] === slot)
        .sort(
          (a, b) =>
            a.order - b.order ||
            FEATURES.findIndex((f) => f.id === a.id) -
              FEATURES.findIndex((f) => f.id === b.id),
        ),
  };
}

// ── checkProfile ────────────────────────────────────────────────────────

export interface BrandProblem {
  /** `error` fails a build. `note` is worth saying and nothing more. */
  readonly level: "error" | "note";
  /** Where in the document, as a JSON pointer — `/features/journal/slots/mobile`. */
  readonly path: string;
  readonly message: string;
}

/**
 * Everything wrong with a profile that the schema cannot see.
 *
 * The schema answers *well-formed*; this answers *meaningful*, because the list
 * of features that exist lives in the registry and the registry lives here.
 * Splitting it that way is deliberate — enumerating the ids in the schema as
 * well would be a second copy of this list, and the copies would drift in the
 * silent direction: the schema would go on validating a profile that names a
 * feature somebody deleted.
 *
 * Run it in the build, where an error should stop the deploy, and in the studio
 * on every keystroke, where it is the reason the Save button is grey. Both read
 * the same function so a profile cannot be savable and unbuildable at once.
 *
 * Assumes the document already passed `validateBrand` — it checks meaning, not
 * shape, and a caller that skips the schema gets a thinner answer, not a throw.
 */
export function checkProfile(
  profile: BrandProfile,
  /**
   * The brand's own theme documents, where the caller has them.
   *
   * Optional because the two callers differ: the studio and the build have read
   * the files and can be told whether `themes.default` names one, while a test
   * handing in a profile alone gets the checks that need no documents. The
   * difference is narrow and stated at each use rather than pretended away.
   */
  custom: readonly Theme[] = [],
): BrandProblem[] {
  const problems: BrandProblem[] = [];

  const themes = profile.themes;
  if (themes) {
    const customIds = custom.map((t) => t.id);
    const offersBuiltins = themes.allow === undefined || themes.allow.length > 0;
    const offersCustom = (themes.custom?.length ?? 0) > 0;

    // `allow: []` with nothing of its own is an app whose theme picker is empty.
    // Legal against the schema, and never what anybody meant.
    if (!offersBuiltins && !offersCustom) {
      problems.push({
        level: "error",
        path: "/themes/allow",
        message:
          "allow is empty and no custom themes are listed, so this app would " +
          "offer no themes at all. Name some built-ins, or ship your own.",
      });
    }

    const wanted = themes.default?.trim();
    if (wanted && themes.allow !== undefined) {
      // Only decidable when the built-ins are named: with `allow` absent the
      // default may be any shipped theme, and this module cannot see that list.
      const offered = themes.allow.includes(wanted) || customIds.includes(wanted);
      // A custom id is unknowable when the documents were not supplied, so a
      // brand that ships themes is given the benefit of the doubt rather than a
      // false accusation.
      if (!offered && (custom.length > 0 || !offersCustom)) {
        problems.push({
          level: "error",
          path: "/themes/default",
          message:
            `"${wanted}" is not a theme this brand offers — the app would open ` +
            "in a theme its own picker will not list.",
        });
      }
    }
  }

  if (profile.identity?.name && !profile.identity.shortName) {
    const name = profile.identity.name.trim();
    if (name.length > 12) {
      problems.push({
        level: "error",
        path: "/identity/shortName",
        message:
          `"${name}" is ${name.length} characters and becomes the PWA short_name, ` +
          "which a phone truncates under the home-screen icon at about 12. " +
          "Give a shorter shortName.",
      });
    }
  }

  const overrides = profile.features ?? {};

  for (const [id, over] of Object.entries(overrides)) {
    if (!over) continue;
    const def = featureById(id);
    if (!def) {
      problems.push({
        level: "error",
        path: `/features/${id}`,
        message: `no feature "${id}" — it was renamed, deleted, or misspelt.`,
      });
      continue;
    }

    if (def.core && over.enabled === false) {
      problems.push({
        level: "error",
        path: `/features/${id}/enabled`,
        message: `"${def.label}" is core and cannot be switched off.`,
      });
    }

    for (const shell of SHELLS) {
      const asked = over.slots?.[shell];
      if (!asked) continue;
      const allowed = slotsFor(def, shell);
      if (allowed.length === 0) {
        problems.push({
          level: "error",
          path: `/features/${id}/slots/${shell}`,
          message: `"${def.label}" has no ${shell} chrome, so it cannot be placed there.`,
        });
      } else if (!(allowed as readonly string[]).includes(asked)) {
        problems.push({
          level: "error",
          path: `/features/${id}/slots/${shell}`,
          message:
            `"${def.label}" cannot go in ${shell}.${asked} — ` +
            `it may occupy ${allowed.join(", ")}.`,
        });
      }
    }
  }

  // Capacity, and the cascade, are both properties of the resolved whole rather
  // than of any one entry, so they are read off the resolution instead of
  // recomputed — a second implementation here is how the two would come to
  // disagree about what the app will actually do.
  const resolved = resolveBrand(profile);

  for (const shell of SHELLS) {
    const counts = new Map<AnySlot, ResolvedFeature[]>();
    for (const feature of Object.values(resolved.features)) {
      if (!feature.enabled) continue;
      const slot = feature.slots[shell];
      if (!slot) continue;
      const held = counts.get(slot);
      if (held) held.push(feature);
      else counts.set(slot, [feature]);
    }
    for (const [slot, held] of counts) {
      const cap = capacityOf(shell, slot);
      if (cap !== null && held.length > cap) {
        problems.push({
          level: "error",
          path: `/features`,
          message:
            `${shell}.${slot} holds ${cap} and ${held.length} were placed there ` +
            `(${held.map((f) => f.label).join(", ")}). The overflow renders elsewhere.`,
        });
      }
    }
  }

  for (const feature of Object.values(resolved.features)) {
    if (feature.disabledBy) {
      problems.push({
        level: "note",
        path: `/features/${feature.id}`,
        message: `"${feature.label}" is off because "${feature.disabledBy}" is off.`,
      });
    }
  }

  return problems;
}

/** Whether a set of problems should stop a build. */
export function hasErrors(problems: readonly BrandProblem[]): boolean {
  return problems.some((p) => p.level === "error");
}

// ── compactProfile ──────────────────────────────────────────────────────

/**
 * Drop everything a profile says that the registry already says.
 *
 * **A profile states only its differences**, and that is not a style rule — it
 * is what makes a new feature appear in every brand automatically, and what
 * makes a diff between two brands the list of things that actually differ. An
 * editor that wrote out every field would freeze today's defaults into every
 * file: change the registry later and the brands would not follow, silently,
 * because each one now asserts the old value.
 *
 * So this is the inverse of `resolveBrand`, and the pair has a property worth
 * stating: `resolveBrand(compactProfile(p))` equals `resolveBrand(p)`, for
 * every `p`. Compacting can never change what gets built — it only removes
 * statements that were already true.
 *
 * Written for the studio, which would otherwise serialise its whole form.
 */
export function compactProfile(profile: BrandProfile): BrandProfile {
  const identity: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(profile.identity ?? {})) {
    if (typeof value === "string" && !value.trim()) continue;
    if (value === undefined || value === null) continue;
    identity[field] = typeof value === "string" ? value.trim() : value;
  }
  // `shortName` that merely repeats the name is what `resolveBrand` already
  // defaults to, so storing it is a second copy of the name to keep in step.
  if (identity.shortName === identity.name) delete identity.shortName;

  const features: Record<string, BrandFeatureOverride> = {};
  FEATURES.forEach((def, index) => {
    const over = profile.features?.[def.id];
    if (!over) return;
    const kept: BrandFeatureOverride = {};

    // Core is always on, so `enabled` on it says nothing whatever its value.
    if (over.enabled === false && !def.core) kept.enabled = false;

    const label = over.label?.trim();
    if (label && label !== def.label) kept.label = label;

    const slots: { desktop?: AnySlot; mobile?: AnySlot } = {};
    for (const shell of SHELLS) {
      const asked = over.slots?.[shell];
      if (!asked) continue;
      const allowed = slotsFor(def, shell);
      // An illegal slot is dropped rather than written: `resolveBrand` ignores
      // it anyway, so keeping it would put a line in the file that does nothing
      // and reads as though it does.
      if (!(allowed as readonly string[]).includes(asked)) continue;
      if (asked !== defaultSlot(def, shell)) slots[shell] = asked as AnySlot;
    }
    if (Object.keys(slots).length > 0) kept.slots = slots;

    if (over.order !== undefined && over.order !== defaultOrder(def, index)) {
      kept.order = over.order;
    }

    if (Object.keys(kept).length > 0) features[def.id] = kept;
  });

  const compact: BrandProfile = {
    schemaVersion: profile.schemaVersion,
    key: profile.key,
    // `name` is required by the schema and this cannot invent one — a profile
    // arriving without it is malformed, and compacting is not the place that
    // finds out. `checkProfile` and `validateBrand` both say so.
    identity: identity as unknown as BrandProfile["identity"],
  };
  if (profile.backend && Object.keys(profile.backend).length > 0) {
    compact.backend = profile.backend;
  }
  if (profile.themes) {
    // `allow: []` is a real answer — "none of the built-ins" — so emptiness is
    // not the test for whether to keep the block. Only a `themes` saying
    // nothing at all is dropped.
    const { default: byDefault, allow, custom: paths } = profile.themes;
    const kept: NonNullable<BrandProfile["themes"]> = {};
    if (byDefault?.trim()) kept.default = byDefault.trim();
    if (allow !== undefined) kept.allow = allow;
    if (paths && paths.length > 0) kept.custom = paths;
    if (Object.keys(kept).length > 0) compact.themes = kept;
  }
  if (Object.keys(features).length > 0) compact.features = features;
  return compact;
}
